/**
 * Web Hand Detector - MediaPipe Hand Landmarker
 * Detects hand landmarks for gesture-based emotion boosting (e.g. hand near face = focused)
 */

import { HandLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';

export interface HandData {
  /** Is at least one hand detected */
  detected: boolean;
  /** Number of hands detected */
  count: number;
  /** Landmarks of first hand (21 points) */
  landmarks?: Array<{ x: number; y: number; z: number }>;
  /** Is hand near face region (upper half of frame, centered) */
  nearFace: boolean;
}

export class WebHandDetector {
  private handLandmarker?: HandLandmarker;
  private videoElement?: HTMLVideoElement;
  private isInitialized = false;
  private isMobileRuntime = false;

  async initialize(videoElement: HTMLVideoElement, isMobileRuntime = false): Promise<void> {
    this.videoElement = videoElement;
    this.isMobileRuntime = isMobileRuntime;

    try {
      console.log('🖐️ Loading MediaPipe Hand Landmarker...');

      const vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
      );

      this.handLandmarker = await HandLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task',
          delegate: 'GPU',
        },
        runningMode: isMobileRuntime ? 'IMAGE' : 'VIDEO',
        numHands: 1,
        minHandDetectionConfidence: isMobileRuntime ? 0.30 : 0.5,
        minTrackingConfidence: isMobileRuntime ? 0.30 : 0.5,
      });

      this.isInitialized = true;
      console.log('✅ Hand Landmarker loaded');
    } catch (error) {
      console.error('❌ Failed to load Hand Landmarker:', error);
    }
  }

  detect(): HandData {
    const empty: HandData = { detected: false, count: 0, nearFace: false };
    if (!this.isInitialized || !this.handLandmarker || !this.videoElement) return empty;
    if (this.videoElement.readyState < 2) return empty;

    try {
      const result = this.isMobileRuntime
        ? this.handLandmarker.detect(this.videoElement)
        : this.handLandmarker.detectForVideo(this.videoElement, Date.now());

      if (!result.landmarks || result.landmarks.length === 0) return empty;

      const landmarks = result.landmarks[0].map(lm => ({ x: lm.x, y: lm.y, z: lm.z }));

      // Check if hand is near face: wrist (0) or middle finger tip (12) in upper-center of frame
      // Face region: roughly x=0.15-0.85, y=0.0-0.65 (relaxed for desktop webcam)
      const wrist = landmarks[0];
      const middleTip = landmarks[12];
      const nearFace =
        (wrist.y < 0.65 && wrist.x > 0.15 && wrist.x < 0.85) ||
        (middleTip.y < 0.65 && middleTip.x > 0.15 && middleTip.x < 0.85);

      console.log(`[EIE-HAND] detected! wrist=(${wrist.x.toFixed(2)},${wrist.y.toFixed(2)}) tip=(${middleTip.x.toFixed(2)},${middleTip.y.toFixed(2)}) nearFace=${nearFace}`);

      return {
        detected: true,
        count: result.landmarks.length,
        landmarks,
        nearFace,
      };
    } catch {
      return empty;
    }
  }

  async dispose(): Promise<void> {
    if (this.handLandmarker) {
      this.handLandmarker.close();
    }
    this.isInitialized = false;
  }
}
